import { Banner, Button } from "@shopify/polaris"
import React, { useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import Logo from "./Logo.jsx"
import { LoadingScreen } from "./LoadingScreen.jsx"
import { auth } from "../firebase.js"

// OAuth params forwarded by the server from /oauth/authorize.
const OAUTH_PARAM_KEYS = [
	"client_id",
	"redirect_uri",
	"state",
	"scope",
	"resource",
	"code_challenge",
	"code_challenge_method",
]

const MAX_CLIENT_NAME_LENGTH = 60

const getOAuthParams = () => {
	const search = new URLSearchParams(window.location.search)
	const params = {}
	for (const key of OAUTH_PARAM_KEYS) {
		const value = search.get(key)
		if (value != null) params[key] = value
	}
	return params
}

const isFramed = () => {
	try {
		return window.self !== window.top
	} catch {
		return true
	}
}

const cardStyle = {
	background: "white",
	borderRadius: "0.6rem",
	boxShadow: "0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)",
	width: "400px",
	maxWidth: "100%",
	padding: "2rem",
}

const Page = ({ children }) => (
	<div
		style={{
			width: "100%",
			height: "100%",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			padding: "0 1rem",
			gap: "1rem",
		}}
	>
		<Logo lessMargin={true} />
		<div style={cardStyle}>{children}</div>
	</div>
)

const titleStyle = {
	fontSize: "1.1rem",
	fontWeight: 600,
	marginBottom: "0.5rem",
	color: "#202223",
	overflowWrap: "anywhere",
}

const textStyle = { fontSize: "0.85rem", color: "#6d7175" }

/**
 * Consent screen for the MCP OAuth flow, shown to a signed-in Firebase user.
 *
 * The server redirects external MCP clients (e.g. ChatGPT) to /mcp-authorize
 * with the OAuth params in the query string. Anyone can register a client, so
 * nothing is issued until the user clicks Allow: only then is the user's id
 * token exchanged for an authorization code via /oauth/authorize/complete and
 * the browser sent back to the client's redirect URI.
 */
export const McpAuthorize = () => {
	const { t } = useTranslation()
	const [error, setError] = useState(null)
	const [client, setClient] = useState(null)
	const [submitting, setSubmitting] = useState(false)

	useEffect(() => {
		// The Allow button must not be clickable through a third-party frame.
		if (isFramed()) {
			setError(t("mcp_authorize.framed_error"))
			return
		}

		let cancelled = false

		const loadClient = async () => {
			try {
				const query = new URLSearchParams(getOAuthParams())
				const res = await fetch(`/oauth/authorize/client?${query}`)
				const data = await res.json().catch(() => ({}))
				if (cancelled) return
				if (!res.ok || !data.redirect) {
					setError(data.error || t("mcp_authorize.error"))
					return
				}
				setClient(data)
			} catch (e) {
				if (!cancelled) setError(e?.message || t("mcp_authorize.error"))
			}
		}

		loadClient()
		return () => {
			cancelled = true
		}
	}, [])

	const allow = async () => {
		setSubmitting(true)
		try {
			const idToken = await auth.currentUser?.getIdToken()
			if (!idToken) {
				setError(t("mcp_authorize.error"))
				return
			}

			const res = await fetch("/oauth/authorize/complete", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					...getOAuthParams(),
					id_token: idToken,
					consent: "allow",
				}),
			})
			const data = await res.json().catch(() => ({}))

			if (!res.ok || !data.redirect) {
				setError(
					data.error_description ||
						data.error ||
						t("mcp_authorize.error"),
				)
				return
			}

			window.location.href = data.redirect
		} catch (e) {
			setError(e?.message || t("mcp_authorize.error"))
		} finally {
			setSubmitting(false)
		}
	}

	const deny = () => {
		// redirect_uri was validated against the client's registration by
		// /oauth/authorize/client before the buttons were shown.
		const { redirect_uri, state } = getOAuthParams()
		const url = new URL(redirect_uri)
		url.searchParams.set("error", "access_denied")
		if (state) url.searchParams.set("state", state)
		window.location.href = url.toString()
	}

	if (error) {
		return (
			<Page>
				<div style={{ textAlign: "center" }}>
					<h1 style={titleStyle}>{t("mcp_authorize.error_title")}</h1>
					<p style={textStyle}>{error}</p>
				</div>
			</Page>
		)
	}

	if (!client) return <LoadingScreen />

	const { redirect } = client
	const destination = redirect.loopback
		? t("mcp_authorize.local_app", { host: redirect.host })
		: redirect.host
	// client_name is chosen by whoever registered the client, so it is only
	// trusted as the display name when the redirect goes to a known client.
	const clientName =
		redirect.verified && client.client_name
			? client.client_name.slice(0, MAX_CLIENT_NAME_LENGTH)
			: redirect.host

	return (
		<Page>
			<h1 style={titleStyle}>
				{t("mcp_authorize.consent_title", { client: clientName })}
			</h1>
			{auth.currentUser?.email && (
				<p style={{ ...textStyle, marginBottom: "1rem" }}>
					{t("mcp_authorize.signed_in_as", {
						email: auth.currentUser.email,
					})}
				</p>
			)}
			{!redirect.verified && (
				<div style={{ marginBottom: "1rem" }}>
					<Banner status="warning">
						{t("mcp_authorize.unverified_warning")}
					</Banner>
				</div>
			)}
			<p style={{ ...textStyle, marginBottom: "0.5rem" }}>
				{t("mcp_authorize.permissions")}
			</p>
			<p
				style={{
					...textStyle,
					marginBottom: "1.5rem",
					overflowWrap: "anywhere",
				}}
			>
				<Trans
					i18nKey="mcp_authorize.redirect_to"
					values={{ host: destination }}
					components={{ b: <b></b> }}
				/>
			</p>
			<div
				style={{
					display: "flex",
					gap: "0.5rem",
					justifyContent: "flex-end",
				}}
			>
				<Button onClick={deny} disabled={submitting}>
					{t("mcp_authorize.deny")}
				</Button>
				<Button primary onClick={allow} loading={submitting}>
					{t("mcp_authorize.allow")}
				</Button>
			</div>
		</Page>
	)
}

export default McpAuthorize
